import { useState } from "react";
import { Link } from "react-router-dom";
import DatePicker, { DateObject } from "react-multi-date-picker";
import persian from "react-date-object/calendars/persian";
import persian_fa from "react-date-object/locales/persian_fa";
import TimePicker from "react-multi-date-picker/plugins/time_picker";
import { PageBanner } from "@/shared/components/PageBanner";
import { useApproveLeaveRequest, useLeaveRequests, useLeaveTypes, useRejectLeaveRequest, useSubmitLeaveRequest } from "../api";
import { useV1UsersList } from "@/api/generated/v1/v1";
import type { LeaveType, LeaveRequest } from "../types";

export default function LeaveRequestsPage() {
  const { data: leaveTypes = [] } = useLeaveTypes();
  const { data: requests = [] } = useLeaveRequests();
  const submit = useSubmitLeaveRequest();
  const approve = useApproveLeaveRequest();
  const reject = useRejectLeaveRequest();

  const { data: usersData } = useV1UsersList({ page_size: 1000 } as any);
  const users = usersData?.results || [];

  const [form, setForm] = useState({
    operator_user_id: "",
    leave_type: "",
    start_datetime: "",
    end_datetime: "",
    reason: "",
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1.5rem", fontFamily: "var(--font-family-fa, sans-serif)" }} dir="rtl">
      <PageBanner
        title="درخواست و تأیید مرخصی"
        subtitle="ثبت درخواست مرخصی اپراتورها و بررسی توسط سرپرست شیفت"
        icon="🌴"
        helpLink="/help/shopfloor/leave-requests"
      >
        <Link to="/production/shopfloor/leave-types" style={{ padding: "6px 12px", borderRadius: "8px", background: "rgba(255, 255, 255, 0.9)", border: "1px solid rgba(255, 255, 255, 1)", color: "#007270", fontSize: "0.85rem", fontWeight: 700, textDecoration: "none", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "6px" }}>
          <span>⚙️</span>
          <span>مدیریت انواع مرخصی</span>
        </Link>
      </PageBanner>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>درخواست جدید</h2>
        </div>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1.25rem", alignItems: "flex-end" }}>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>شناسه اپراتور</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.operator_user_id}
              onChange={(e) => setForm((s) => ({ ...s, operator_user_id: e.target.value }))}
            >
              <option value="">— انتخاب اپراتور —</option>
              {users.map((u: any) => (
                <option key={u.id} value={u.id}>
                  {u.first_name} {u.last_name} ({u.username})
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>نوع مرخصی</label>
            <select
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              value={form.leave_type} onChange={(e) => setForm((s) => ({ ...s, leave_type: e.target.value }))}
            >
              <option value="">— انتخاب کنید —</option>
              {leaveTypes.map((lt: LeaveType) => (
                <option key={lt.id} value={lt.id}>
                  {lt.name} {lt.allow_hourly ? "(ساعتی مجاز)" : ""}
                </option>
              ))}
            </select>
          </div>
          <div style={{ flex: "1 1 200px", position: "relative" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>از تاریخ و ساعت</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              format="YYYY-MM-DD HH:mm"
              plugins={[<TimePicker position="bottom" />]}
              value={form.start_datetime || ""}
              onChange={(date: DateObject | null) => {
                setForm((prev) => ({
                  ...prev,
                  start_datetime: date ? date.format("YYYY-MM-DDTHH:mm:00") : "",
                }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب زمان شمسی..."
            />
          </div>
          <div style={{ flex: "1 1 200px", position: "relative" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>تا تاریخ و ساعت</label>
            <DatePicker
              calendar={persian}
              locale={persian_fa}
              calendarPosition="bottom-right"
              portal
              format="YYYY-MM-DD HH:mm"
              plugins={[<TimePicker position="bottom" />]}
              value={form.end_datetime || ""}
              onChange={(date: DateObject | null) => {
                setForm((prev) => ({
                  ...prev,
                  end_datetime: date ? date.format("YYYY-MM-DDTHH:mm:00") : "",
                }));
              }}
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none", height: "auto" }}
              containerStyle={{ width: "100%" }}
              placeholder="انتخاب زمان شمسی..."
            />
          </div>
          <div style={{ flex: "1 1 100%" }}>
            <label style={{ fontSize: "0.85rem", fontWeight: 700, color: "#2B2B2B", display: "block", marginBottom: "4px" }}>دلیل</label>
            <input
              style={{ width: "100%", padding: "8px 12px", borderRadius: "10px", border: "1px solid var(--color-border, #E4DCC8)", background: "#FFFFFF", color: "#2B2B2B", fontSize: "0.85rem", outline: "none" }}
              placeholder="توضیحات مرخصی"
              value={form.reason}
              onChange={(e) => setForm((s) => ({ ...s, reason: e.target.value }))}
            />
          </div>
          <div style={{ flex: "1 1 100%", display: "flex", justifyContent: "flex-end" }}>
            <button style={{ padding: "8px 16px", borderRadius: "10px", background: "#007270", color: "#FFF", border: "none", fontSize: "0.85rem", fontWeight: 700, cursor: submit.isPending ? "not-allowed" : "pointer", opacity: submit.isPending ? 0.7 : 1 }} onClick={() => submit.mutate(form)} disabled={submit.isPending}>
              ثبت درخواست
            </button>
          </div>
        </div>
      </section>

      <section className="brand-card-extand" style={{ padding: "1.5rem", border: "1px solid var(--color-border, #E4DCC8)" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingBottom: "0.75rem", marginBottom: "1.25rem", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
          <h2 style={{ margin: 0, fontSize: "1.1rem", fontWeight: 800, color: "#007270" }}>درخواست‌های مرخصی</h2>
        </div>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem", textAlign: "right" }}>
            <thead>
              <tr style={{ background: "rgba(0,0,0,0.04)", borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>اپراتور</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>از</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>تا</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>وضعیت</th>
                <th style={{ padding: "12px 10px", fontWeight: 700, color: "#2B2B2B" }}>عملیات</th>
              </tr>
            </thead>
            <tbody>
              {requests.map((r: LeaveRequest) => {
                const op = users.find((u: any) => u.id === r.operator_user_id);
                const operatorName = op ? `${op.first_name} ${op.last_name}` : r.operator_user_id;

                return (
                  <tr key={r.id} style={{ borderBottom: "1px solid var(--color-border, #E4DCC8)" }}>
                    <td style={{ padding: "12px 10px", color: "#444", fontWeight: 600 }}>{operatorName}</td>
                    <td style={{ padding: "12px 10px", color: "#444", direction: "ltr", textAlign: "right" }}>{r.start_datetime.replace('T', ' ').substring(0, 16)}</td>
                    <td style={{ padding: "12px 10px", color: "#444", direction: "ltr", textAlign: "right" }}>{r.end_datetime.replace('T', ' ').substring(0, 16)}</td>
                    <td style={{ padding: "12px 10px" }}>
                      <span style={{ padding: "4px 8px", borderRadius: "6px", fontSize: "0.75rem", fontWeight: 700, background: r.status === 'submitted' ? "rgba(245, 158, 11, 0.15)" : r.status === 'approved' ? "rgba(16, 185, 129, 0.15)" : "rgba(239, 68, 68, 0.15)", color: r.status === 'submitted' ? "#D97706" : r.status === 'approved' ? "#10B981" : "#EF4444" }}>
                        {r.status === 'submitted' ? 'در انتظار تأیید' : r.status === 'approved' ? 'تأیید شده' : 'رد شده'}
                      </span>
                    </td>
                    <td style={{ padding: "12px 10px" }}>
                      {r.status === "submitted" && (
                        <div style={{ display: "flex", gap: "6px" }}>
                          <button style={{ padding: "6px 12px", borderRadius: "8px", background: "rgba(16, 185, 129, 0.1)", border: "1px solid rgba(16, 185, 129, 0.2)", color: "#10B981", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }} onClick={() => approve.mutate(r.id)}>
                            تأیید
                          </button>
                          <button style={{ padding: "6px 12px", borderRadius: "8px", background: "rgba(239, 68, 68, 0.1)", border: "1px solid rgba(239, 68, 68, 0.2)", color: "#EF4444", fontSize: "0.8rem", fontWeight: 700, cursor: "pointer", transition: "all 0.2s" }} onClick={() => reject.mutate({ id: r.id })}>
                            رد
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
              {requests.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "#888" }}>هیچ درخواستی یافت نشد.</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
